import { Component, OnInit } from '@angular/core';
import { ThemingService } from 'src/app/theming.service';
import { ApiService } from 'src/app/api.service';
import {
  cloneProductViewTemplate,
  ProductTemplateLabel,
  ProductViewTemplatePayload,
} from 'src/app/helpers/product-view-template';

declare const $: any;

type ProductTemplateTranslationField =
  | 'templateName'
  | 'quantity'
  | 'buyNow'
  | 'addToCart'
  | 'descriptionTab'
  | 'conditionsTab';
type ProductTemplateLanguage = 'es' | 'en';
type ProductTemplateTranslatedField = {
  value: string;
  manual: boolean;
};

@Component({
  selector: 'app-product-view-template-editor',
  templateUrl: './product-view-template-editor.component.html',
  styleUrls: ['./product-view-template-editor.component.scss'],
})
export class ProductViewTemplateEditorComponent implements OnInit {
  loading = false;
  saving = false;
  showErrors = false;
  usesLocalDefault = false;
  successMessageKey = '';
  errorMessageKey = '';
  infoMessageKey = '';

  templateData: ProductViewTemplatePayload = cloneProductViewTemplate();
  private lastSavedSnapshot: ProductViewTemplatePayload = cloneProductViewTemplate();
  selectedLanguage: ProductTemplateLanguage = 'es';

  constructor(
    private apiService: ApiService,
    private themingService: ThemingService
  ) {}

  ngOnInit(): void {
    void this.loadTemplate();
  }

  async loadTemplate(): Promise<void> {
    this.loading = true;
    this.showErrors = false;
    this.clearMessages();

    try {
      const res: any = await this.apiService.getProductViewTemplate(this.themingService.shopId);
      const row = this.extractTemplateRow(res);

      if (!row) {
        this.applyLocalDefault();
        return;
      }

      this.templateData = this.normalizeTemplateRow(row);
      this.lastSavedSnapshot = cloneProductViewTemplate(this.templateData);
      this.usesLocalDefault = false;
    } catch (error) {
      console.error('GET product view template error', error);
      this.applyLocalDefault();
    } finally {
      this.loading = false;
    }
  }

  get sections(): any {
    return this.templateData.configJson.sections;
  }

  get price(): any {
    return this.sections.price;
  }

  get quantity(): any {
    return this.sections.quantity;
  }

  get buyNow(): any {
    return this.sections.actions.buyNow;
  }

  get addToCart(): any {
    return this.sections.actions.addToCart;
  }

  get descriptionTab(): any {
    return this.sections.tabs.description;
  }

  get conditionsTab(): any {
    return this.sections.tabs.conditions;
  }

  get isEnglishMode(): boolean {
    return this.selectedLanguage === 'en';
  }

  selectLanguage(language: ProductTemplateLanguage): void {
    this.selectedLanguage = language;
    if (language === 'en') {
      this.ensureEnglishTranslatedContent();
    }
  }

  getTemplateNameValue(): string {
    return this.isEnglishMode
      ? this.getEnglishTranslationValue('templateName')
      : this.templateData.templateName;
  }

  setTemplateNameValue(value: string): void {
    if (this.isEnglishMode) {
      this.updateEnglishTranslationValue('templateName', value);
      return;
    }

    this.templateData.templateName = value || '';
  }

  getLabelValue(field: ProductTemplateTranslationField, labelField: ProductTemplateLabel): string {
    return this.isEnglishMode
      ? this.getEnglishTranslationValue(field)
      : labelField.value;
  }

  setLabelValue(field: ProductTemplateTranslationField, labelField: ProductTemplateLabel, value: string): void {
    if (this.isEnglishMode) {
      this.updateEnglishTranslationValue(field, value);
      return;
    }

    labelField.value = value || '';
  }

  isEnglishFieldManual(field: ProductTemplateTranslationField): boolean {
    return this.getEnglishTranslatedField(field).manual;
  }

  onManualFieldToggle(field: ProductTemplateTranslationField, event: Event): void {
    this.getEnglishTranslatedField(field).manual = (event.target as HTMLInputElement).checked;
  }

  async saveTemplate(): Promise<void> {
    this.clearMessages();
    this.showErrors = true;

    if (!this.isTemplateValid()) {
      this.selectedLanguage = 'es';
      this.errorMessageKey = 'ALL_FIELDS_REQUIRED';
      return;
    }

    this.saving = true;
    try {
      const payload = this.buildPayload();
      const res: any = await this.apiService.saveProductViewTemplate(
        this.themingService.shopId,
        payload
      );
      const ok = this.isSuccessfulSaveResponse(res);

      if (!ok) {
        this.errorMessageKey = 'ERROR_SAVING_CHANGES';
        return;
      }

      this.templateData = this.normalizeTemplateRow(res.template);
      this.lastSavedSnapshot = cloneProductViewTemplate(this.templateData);
      this.usesLocalDefault = false;
      this.clearMessages();
      this.openModal('#productTemplateSuccessModal');
    } catch (error) {
      console.error('PUT product view template error', error);
      this.errorMessageKey = 'PRODUCT_TEMPLATE_SAVE_ERROR';
    } finally {
      this.saving = false;
    }
  }

  async restoreDefaults(): Promise<void> {
    this.clearMessages();
    this.showErrors = false;
    this.templateData = cloneProductViewTemplate();
    this.lastSavedSnapshot = cloneProductViewTemplate();
    this.usesLocalDefault = true;
    this.selectedLanguage = 'es';
    this.infoMessageKey = 'PRODUCT_TEMPLATE_LOCAL_DEFAULT_INFO';

    this.saving = true;
    try {
      await this.apiService.deleteProductViewTemplate(this.themingService.shopId);
      this.successMessageKey = 'PRODUCT_TEMPLATE_DEFAULTS_RESTORED';
    } catch (error) {
      console.error('RESET product view template error', error);
      this.infoMessageKey = 'PRODUCT_TEMPLATE_LOCAL_DEFAULT_SAVE_INFO';
    } finally {
      this.saving = false;
    }
  }

  cancelChanges(): void {
    this.clearMessages();
    this.showErrors = false;
    this.templateData = cloneProductViewTemplate(this.lastSavedSnapshot);
  }

  closeModal(): void {
    this.hideModal('#productTemplateSuccessModal');
  }

  getEnglishTranslationValue(field: ProductTemplateTranslationField): string {
    return this.getEnglishTranslatedField(field).value;
  }

  updateEnglishTranslationValue(field: ProductTemplateTranslationField, value: string): void {
    this.getEnglishTranslatedField(field).value = value || '';
  }

  isQuantityLabelInvalid(): boolean {
    return this.showErrors && this.quantity.visible && !this.hasValue(this.quantity.label?.value);
  }

  isBuyNowLabelInvalid(): boolean {
    return this.showErrors && this.buyNow.visible && !this.hasValue(this.buyNow.label?.value);
  }

  isAddToCartLabelInvalid(): boolean {
    return this.showErrors && this.addToCart.visible && !this.hasValue(this.addToCart.label?.value);
  }

  isDescriptionLabelInvalid(): boolean {
    return this.showErrors && this.descriptionTab.visible && !this.hasValue(this.descriptionTab.label?.value);
  }

  isConditionsLabelInvalid(): boolean {
    return this.showErrors && this.conditionsTab.visible && !this.hasValue(this.conditionsTab.label?.value);
  }

  private buildPayload(): ProductViewTemplatePayload {
    const payload = cloneProductViewTemplate(this.templateData);
    payload.isActive = payload.isActive ? 1 : 0;
    payload.translatedContent = this.resolveTranslatedContentPayload();
    return payload;
  }

  private isTemplateValid(): boolean {
    return this.hasValue(this.templateData.templateName)
      && !this.isQuantityLabelInvalid()
      && !this.isBuyNowLabelInvalid()
      && !this.isAddToCartLabelInvalid()
      && !this.isDescriptionLabelInvalid()
      && !this.isConditionsLabelInvalid();
  }

  private extractTemplateRow(res: any): any | null {
    if (!res || res?.status >= 400 || res?.statusCode >= 400) {
      return null;
    }

    if (res?.message === 'success' && 'template' in res) {
      return res.template || null;
    }

    if (Array.isArray(res?.data)) {
      return res.data[0] || null;
    }

    if (res?.data) {
      return res.data;
    }

    if (res?.template) {
      return res.template;
    }

    return res?.configJson || res?.config_json ? res : null;
  }

  private normalizeTemplateRow(row: any): ProductViewTemplatePayload {
    const normalized = cloneProductViewTemplate();
    const rawConfig = this.parseJsonField(row.configJson ?? row.config_json) || {};
    const configJson = this.normalizeConfigJson(rawConfig, normalized.configJson);

    return this.mergeDeep(normalized, {
      templateName: row.templateName ?? row.template_name ?? normalized.templateName,
      isActive: (row.isActive ?? row.is_active ?? normalized.isActive) ? 1 : 0,
      configJson,
      translatedContent: this.normalizeTranslatedContent(row.translatedContent ?? row.translated_content ?? null),
    });
  }

  private normalizeConfigJson(rawConfig: any, fallbackConfig: any): any {
    const rawSections = rawConfig?.sections || {};
    const normalized = cloneProductViewTemplate({
      templateName: '',
      isActive: 1,
      configJson: fallbackConfig,
      translatedContent: null,
    }).configJson;

    normalized.version = rawConfig?.version || 1;
    normalized.sections.price.visible = rawSections.price?.visible !== false;
    normalized.sections.quantity.visible = rawSections.quantity?.visible !== false;
    normalized.sections.quantity.label = this.normalizeLabel(rawSections.quantity?.label, normalized.sections.quantity.label.defaultValue);

    const rawBuyNow = rawSections.actions?.buyNow || rawSections.actions?.primaryButton;
    const rawAddToCart = rawSections.actions?.addToCart || rawSections.actions?.secondaryButton;
    normalized.sections.actions.buyNow.visible = rawBuyNow?.visible !== false;
    normalized.sections.actions.buyNow.label = this.normalizeLabel(rawBuyNow?.label, normalized.sections.actions.buyNow.label.defaultValue);
    normalized.sections.actions.addToCart.visible = rawAddToCart?.visible !== false;
    normalized.sections.actions.addToCart.label = this.normalizeLabel(rawAddToCart?.label, normalized.sections.actions.addToCart.label.defaultValue);

    normalized.sections.tabs.description.visible = rawSections.tabs?.description?.visible !== false;
    normalized.sections.tabs.description.label = this.normalizeLabel(rawSections.tabs?.description?.label, normalized.sections.tabs.description.label.defaultValue);
    normalized.sections.tabs.conditions.visible = rawSections.tabs?.conditions?.visible !== false;
    normalized.sections.tabs.conditions.label = this.normalizeLabel(rawSections.tabs?.conditions?.label, normalized.sections.tabs.conditions.label.defaultValue);

    return normalized;
  }

  private normalizeLabel(rawLabel: any, defaultValue: string): ProductTemplateLabel {
    if (rawLabel && typeof rawLabel === 'object') {
      return {
        value: rawLabel.value ?? rawLabel.defaultValue ?? defaultValue,
        defaultValue: rawLabel.defaultValue ?? defaultValue,
      };
    }

    return {
      value: rawLabel ?? defaultValue,
      defaultValue,
    };
  }

  private applyLocalDefault(): void {
    this.templateData = cloneProductViewTemplate();
    this.lastSavedSnapshot = cloneProductViewTemplate();
    this.usesLocalDefault = true;
    this.infoMessageKey = 'PRODUCT_TEMPLATE_LOCAL_DEFAULT_INFO';
  }

  private parseJsonField(value: any): any | null {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch (error) {
        console.error('Invalid product view template JSON payload:', error);
        return null;
      }
    }

    return typeof value === 'object' ? value : null;
  }

  private isSuccessfulSaveResponse(res: any): boolean {
    return !!res && res.message === 'success' && !!res.template;
  }

  private normalizeTranslatedContent(value: any): any {
    const parsed = this.parseJsonField(value) || value;
    const english = parsed?.en;

    if (!english || typeof english !== 'object') {
      return null;
    }

    return {
      en: {
        templateName: this.normalizeTranslatedField(english.templateName),
        labels: {
          quantity: this.normalizeTranslatedField(english.labels?.quantity),
          buyNow: this.normalizeTranslatedField(english.labels?.buyNow),
          addToCart: this.normalizeTranslatedField(english.labels?.addToCart),
          descriptionTab: this.normalizeTranslatedField(english.labels?.descriptionTab),
          conditionsTab: this.normalizeTranslatedField(english.labels?.conditionsTab),
        },
      },
    };
  }

  private ensureEnglishTranslatedContent(): any {
    const current = this.normalizeTranslatedContent(this.templateData.translatedContent) || {};
    current.en = current.en || {};
    current.en.labels = current.en.labels || {};
    current.en.templateName = this.normalizeTranslatedField(current.en.templateName);

    const labels = current.en.labels;
    labels.quantity = this.normalizeTranslatedField(labels.quantity);
    labels.buyNow = this.normalizeTranslatedField(labels.buyNow);
    labels.addToCart = this.normalizeTranslatedField(labels.addToCart);
    labels.descriptionTab = this.normalizeTranslatedField(labels.descriptionTab);
    labels.conditionsTab = this.normalizeTranslatedField(labels.conditionsTab);

    this.templateData.translatedContent = current;
    return current.en;
  }

  private getEnglishTranslatedField(field: ProductTemplateTranslationField): ProductTemplateTranslatedField {
    const english = this.ensureEnglishTranslatedContent();
    return field === 'templateName'
      ? english.templateName
      : english.labels[field];
  }

  private normalizeTranslatedField(field: any): ProductTemplateTranslatedField {
    return {
      value: field && typeof field === 'object' ? field.value || '' : '',
      manual: field && typeof field === 'object' ? field.manual === true : false,
    };
  }

  private resolveTranslatedContentPayload(): any {
    return {
      en: JSON.parse(JSON.stringify(this.ensureEnglishTranslatedContent())),
    };
  }

  private mergeDeep<T>(target: T, source: any): T {
    if (!source || typeof source !== 'object') {
      return target;
    }

    Object.keys(source).forEach((key) => {
      const sourceValue = source[key];
      const targetValue = (target as any)[key];

      if (
        sourceValue &&
        typeof sourceValue === 'object' &&
        !Array.isArray(sourceValue) &&
        targetValue &&
        typeof targetValue === 'object' &&
        !Array.isArray(targetValue)
      ) {
        this.mergeDeep(targetValue, sourceValue);
      } else {
        (target as any)[key] = sourceValue;
      }
    });

    return target;
  }

  private hasValue(value: any): boolean {
    return value !== null && value !== undefined && String(value).trim().length > 0;
  }

  private clearMessages(): void {
    this.successMessageKey = '';
    this.errorMessageKey = '';
    this.infoMessageKey = '';
  }

  private openModal(selector: string): void {
    if (typeof $ !== 'undefined') {
      $(selector).modal('show');
    }
  }

  private hideModal(selector: string): void {
    if (typeof $ !== 'undefined') {
      $(selector).modal('hide');
    }
  }
}
